import type { Canale, Modulo } from './canale';
import type { ParametriRegolazione } from './regolazione';

export type TipoCarico = 'ELETTROVALVOLA' | 'RELE_ON_OFF' | 'VENTILCONVETTORE';

export const TIPI_CARICO: readonly TipoCarico[] = ['ELETTROVALVOLA', 'RELE_ON_OFF', 'VENTILCONVETTORE'];

export type IconaZona =
  | 'soggiorno'
  | 'cucina'
  | 'camera'
  | 'cameretta'
  | 'bagno'
  | 'studio'
  | 'veranda'
  | 'taverna'
  | 'altro';

export interface LimitiSetpoint {
  min: number;
  max: number;
  /** Chi ha impostato i limiti: serve a spiegarlo all'utente (regolazione-utente.md). */
  impostatiDa: string;
}

export interface Zona {
  id: string;
  nome: string;
  icona: IconaZona;
  ordinamento: number;
  canaleSonda: string;
  canaliAttuatore: string[];
  tipoCarico: TipoCarico;
  abilitata: boolean;
  setpoint: number;
  /** true se l'utente ha impostato la zona singolarmente: il setpoint globale non la tocca (RF-12). */
  sovrascritta: boolean;
  limiti: LimitiSetpoint;
}

export type TipoGeneratore = 'TERMOCAMINO' | 'STUFA_PELLET' | 'POMPA_DI_CALORE' | 'CALDAIA_GAS';

export const TIPI_GENERATORE: readonly TipoGeneratore[] = [
  'POMPA_DI_CALORE',
  'CALDAIA_GAS',
  'STUFA_PELLET',
  'TERMOCAMINO',
];

export const MAX_GENERATORI = 4;

export interface Generatore {
  id: string;
  nome: string;
  tipo: TipoGeneratore;
  canaleMandata: string;
  canaleRitorno: string;
  canaleComando?: string;
  /** 1 = primario nella cascata. */
  priorita: number;
  modulante: boolean;
}

export type TipoSorgente = 'centralina' | 'fotovoltaico' | 'pompa_di_calore' | 'termostati';

/**
 * Sorgente dati: la centralina Ema Comfort oppure un sistema di terzi raggiunto da un
 * connettore. Ogni sorgente ha la propria freschezza (interfaccia unificata, §04).
 */
export interface Sorgente {
  id: string;
  nome: string;
  tipo: TipoSorgente;
  /** Se false il connettore legge soltanto: nessun comando viene accettato. */
  comandabile: boolean;
  descrizione?: string;
}

export type ModalitaSistema = 'RISCALDAMENTO' | 'RAFFRESCAMENTO' | 'AUTOMATICO' | 'SPENTO';

export const MODALITA_SISTEMA: readonly ModalitaSistema[] = [
  'RISCALDAMENTO',
  'RAFFRESCAMENTO',
  'AUTOMATICO',
  'SPENTO',
];

export interface Centralina {
  modello: string;
  firmware: string;
  /** `emacomfort` = centralina propria; `terze_parti` = sistema altrui letto da un connettore. */
  tipo: 'emacomfort' | 'terze_parti';
  host?: string;
}

export interface ConfigSanitario {
  presente: boolean;
  setpoint: number;
  boostMinuti: number;
}

export type ModalitaVmc = 'SPENTA' | 'ACCESA' | 'AUTO';

export interface ConfigVmc {
  presente: boolean;
  modalita: ModalitaVmc;
  sogliaUmidita: number;
}

export interface ConfigEnergia {
  presente: boolean;
  sorgenteId?: string;
  potenzaPiccoKw?: number;
  capacitaBatteriaKwh?: number;
}

export interface Indirizzo {
  via: string;
  citta: string;
  provincia?: string;
}

export interface Impianto {
  id: string;
  /** Organizzazione dell'installatore a cui l'impianto appartiene. */
  tenantId: string;
  nome: string;
  indirizzo: Indirizzo;
  cliente: string;
  dataInstallazione: string;
  tecnicoReferente: string;
  centralina: Centralina;
  moduli: Modulo[];
  canali: Canale[];
  zone: Zona[];
  generatori: Generatore[];
  sorgenti: Sorgente[];
  sanitario: ConfigSanitario;
  vmc: ConfigVmc;
  energia: ConfigEnergia;
  parametri: ParametriRegolazione;
  modalita: ModalitaSistema;
}

/** Canale assegnato a una funzione d'impianto, se esiste. */
export function canalePerFunzione(
  impianto: Pick<Impianto, 'canali'>,
  funzione: Canale['assegnazione'] extends infer A ? (A extends { tipo: 'funzione'; funzione: infer F } ? F : never) : never,
  generatoreId?: string,
): Canale | undefined {
  return impianto.canali.find(
    (c) =>
      c.assegnazione.tipo === 'funzione' &&
      c.assegnazione.funzione === funzione &&
      (generatoreId === undefined || c.assegnazione.generatoreId === generatoreId),
  );
}

export function zoneOrdinate(impianto: Pick<Impianto, 'zone'>): Zona[] {
  return [...impianto.zone].sort((a, b) => a.ordinamento - b.ordinamento);
}

export function generatoriPerPriorita(impianto: Pick<Impianto, 'generatori'>): Generatore[] {
  return [...impianto.generatori].sort((a, b) => a.priorita - b.priorita);
}
